import React, { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";
import api from "../../services/api";
import { Toast } from "../../shared/Toast";

const csvHeaders = [
  { label: "Date Of Campaign", key: "date_of_campaign" },
  { label: "3 weeks Packages", key: "packages_3_weeks" },
  { label: "4 weeks Packages", key: "packages_4_weeks" },
  { label: "6 weeks Packages", key: "packages_6_weeks" },
  { label: "9 weeks Packages", key: "packages_9_weeks" },
  { label: "12 weeks Packages", key: "packages_12_weeks" },
  { label: "Total", key: "total" },
];

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const TEAM_NAMES = {
  5: "Alpha",
  2: "Local Telugu",
  "-1": "Classifieds",
};

function AlphaWeekReports({ month, year }) {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(5);
  const csvLinkRef = useRef(null);

  const fetchAlphaWeekReport = async () => {
    setLoading(true);
    try {
      const res = await api({
        url: "/reports/alphaWeekReport",
        method: "POST",
        responseType: "json",
        data: { month, year, team: selectedTeam },
      });

      if (res?.code === 200 && Array.isArray(res.data)) {
        setReportData(res.data);
      } else {
        setReportData([]);
        if (res?.code !== 200) {
          Toast("error", "Unable to fetch Alpha Week report");
        }
      }
    } catch (error) {
      console.error("AlphaWeekReports fetch error:", error);
      Toast("error", "Unable to load report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlphaWeekReport();
  }, [month, year, selectedTeam]);

  const handleDownload = () => {
    if (!reportData.length) {
      Toast("error", "No report data to download");
      return;
    }
    setDownloadLoading(true);
    setTimeout(() => {
      csvLinkRef?.current?.link?.click();
      setDownloadLoading(false);
    }, 100);
  };

  const totals = reportData.reduce(
    (acc, row) => {
      acc.packages_3_weeks += Number(row.packages_3_weeks) || 0;
      acc.packages_4_weeks += Number(row.packages_4_weeks) || 0;
      acc.packages_6_weeks += Number(row.packages_6_weeks) || 0;
      acc.packages_9_weeks += Number(row.packages_9_weeks) || 0;
      acc.packages_12_weeks += Number(row.packages_12_weeks) || 0;
      acc.total += Number(row.total) || 0;
      return acc;
    },
    {
      packages_3_weeks: 0,
      packages_4_weeks: 0,
      packages_6_weeks: 0,
      packages_9_weeks: 0,
      packages_12_weeks: 0,
      total: 0,
    },
  );

  const teamName = TEAM_NAMES[String(selectedTeam)] || "Alpha";
  const monthName = MONTH_NAMES[Number(month) - 1] || "";
  const reportTitle = `${teamName} Ads Packages Report - ${monthName} ${year}`;

  const displayVal = (val) => (Number(val) ? Number(val) : "0");

  return (
    <div className="p-4">
      <div className="flex items-center gap-3 mb-4">
        <select
          className="form-select max-w-sm"
          value={selectedTeam}
          onChange={(e) => setSelectedTeam(e.target.value)}
        >
          <option value={5}>Alpha</option>
          <option value={2}>Local Telugu</option>
          <option value={-1}>Classifieds</option>
        </select>
        <button
          className="btn btn-primary"
          onClick={handleDownload}
          disabled={loading || downloadLoading}
        >
          {downloadLoading ? "Preparing..." : "Download CSV"}
        </button>
      </div>

      <div className="alpha-week-table text-center">
        <table className="w-full text-2xl">
          <thead>
            <tr>
              <td
                colSpan={7}
                className="!bg-blue-700 text-white text-center font-bold py-2 px-4 text-xl border border-black"
              >
                {reportTitle}
              </td>
            </tr>
            <tr>
              {[
                "Date Of Campaign",
                "3 weeks  Packages",
                "4 weeks  Packages",
                "6 weeks  Packages",
                "9 weeks  Packages",
                "12 weeks  Packages",
              ].map((col) => (
                <th
                  key={col}
                  className="!bg-blue-800 text-white font-bold whitespace-nowrap border border-black"
                >
                  {col}
                </th>
              ))}
              <th className="!bg-yellow-200 font-bold whitespace-nowrap border border-black">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {reportData.length ? (
              reportData.map((row, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="text-left font-medium border border-black">
                    {row.date_of_campaign || "-"}
                  </td>
                  <td className="border border-black">{displayVal(row.packages_3_weeks)}</td>
                  <td className="border border-black">{displayVal(row.packages_4_weeks)}</td>
                  <td className="border border-black">{displayVal(row.packages_6_weeks)}</td>
                  <td className="border border-black">{displayVal(row.packages_9_weeks)}</td>
                  <td className="border border-black">{displayVal(row.packages_12_weeks)}</td>
                  <td className="border border-black font-bold">{displayVal(row.total)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="py-6 text-gray-500 border border-black text-center">
                  {loading
                    ? "Loading report..."
                    : "No data available for selected month"}
                </td>
              </tr>
            )}
          </tbody>
          {reportData.length ? (
            <tfoot>
              <tr>
                <td className="!bg-yellow-200 font-bold border border-black"></td>
                <td className="!bg-yellow-200 font-bold border border-black">
                  {totals.packages_3_weeks}
                </td>
                <td className="!bg-yellow-200 font-bold border border-black">
                  {totals.packages_4_weeks}
                </td>
                <td className="!bg-yellow-200 font-bold border border-black">
                  {totals.packages_6_weeks}
                </td>
                <td className="!bg-yellow-200 font-bold border border-black">
                  {totals.packages_9_weeks}
                </td>
                <td className="!bg-yellow-200 font-bold border border-black">
                  {totals.packages_12_weeks}
                </td>
                <td className="!bg-yellow-200 font-bold border border-black">
                  {totals.total}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <CSVLink
        data={reportData}
        headers={csvHeaders}
        filename={`${teamName}WeekReport_${year}_${month}.csv`}
        ref={csvLinkRef}
        className="d-none"
      />
    </div>
  );
}

export default AlphaWeekReports;
